import ActivityIcon from '@clearkit/icons/glyphs/Clock';
import CompanyIcon from '@clearkit/icons/glyphs/Companies';
import PeopleIcon from '@clearkit/icons/glyphs/People';
import SegmentsIcon from '@clearkit/icons/glyphs/Segment';
import {
  CKCompanyLogo,
  CKDrawer,
  CKTab,
  CKTabList,
  CKTabs,
} from '@clearkit/react';
import some from 'lodash/some';
import { Helmet } from 'react-helmet';

import AttributeBrowser from '~/components/AttributeBrowser';
import GraphqlError from '~/components/GraphqlError';
import wrapError from '~/components/JavascriptError/JavascriptErrorHandler';
import LoadingArea from '~/components/LoadingArea';
import MissingPage from '~/components/MissingPage';
import { useFancyQuery } from '~/lib/graphql';
import { paginateData } from '~/lib/graphql/pagination';

import Actions from './Actions';
import Activity from './Activity';
import CompanyDetails from './Details';
import People from './People';
import query from './query.graphql';
import Segments from './Segments';

const Company = ({ id }) => {
  const {
    data,
    data: { company, salesforceAccountConnector, hubspotAccountConnector } = {},
    error,
    loading,
    refetch,
  } = useFancyQuery(query, {
    variables: { id },
    returnPartialData: true,
  });

  const [tabIndex, setTabIndex] = useState(0);
  const overflowScrollRef = React.useRef();

  const handleTabChange = (stickyHeaderBounds) => {
    const stickyHeaderGap = 28;
    const customOffset =
      stickyHeaderBounds.top - stickyHeaderBounds.height - stickyHeaderGap;
    if (overflowScrollRef.current) {
      overflowScrollRef.current.scrollTo({ top: customOffset });
    }
  };

  if (error) {
    return (
      <div className="x-sidebar">
        <GraphqlError error={error} refetch={refetch} />;
      </div>
    );
  }

  if (!loading && !data.company) return <MissingPage className="x-sidebar" />;

  if ((loading && !data) || !data.company)
    return <LoadingArea loading className="x-sidebar" />;

  const { salesforceAccounts, hubspotCompanies } = paginateData(company, {
    fields: ['salesforceAccounts', 'hubspotCompanies'],
  });

  return (
    <div className="x-sidebar">
      <Helmet title={company.name} />
      <CKDrawer.Header className="border-none">
        <div className="flex items-center flex-1">
          <div className="mr-4">
            <CKCompanyLogo
              domain={company.domain}
              size={56}
              cornerRadius={8}
              className="shadow-sm"
            />
          </div>

          <div>
            <CKDrawer.Heading>
              {company.name || company.domain || 'Loading...'}
            </CKDrawer.Heading>
            <a
              href={`http://${company.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue">
              {company.domain || '...'}
            </a>
          </div>

          <div className="ml-auto">
            <Actions
              hubspotCompanies={hubspotCompanies}
              hubspotOwners={hubspotAccountConnector?.owners}
              salesforceAccounts={salesforceAccounts}
              salesforceAccountConnector={salesforceAccountConnector}
            />
          </div>
        </div>
      </CKDrawer.Header>
      <GraphqlError inline error={error} refetch={refetch} />
      <CKTabs
        aria-label="Company tabs"
        className="flex flex-col flex-grow min-h-0"
        isBordered
        index={tabIndex}
        onChange={setTabIndex}>
        <CKTabList
          className="flex-shrink-0 px-10 -mt-4"
          aria-label="Company tabs">
          <CKTab icon={<CompanyIcon />}>Company details</CKTab>
          <CKTab icon={<ActivityIcon />}>Activity</CKTab>
          <CKTab
            icon={<PeopleIcon />}
            badge={company.people?.totalCount || false}>
            People
          </CKTab>
          <CKTab
            icon={<SegmentsIcon />}
            badge={company.segments?.totalCount || false}>
            Audiences
          </CKTab>
        </CKTabList>

        {tabIndex === 0 && (
          <div className="overflow-y-auto" ref={overflowScrollRef}>
            <CompanyDetails
              company={company}
              salesforceAccounts={salesforceAccounts}
              salesforceAccountConnector={salesforceAccountConnector}
              hubspotCompanies={hubspotCompanies}
              hubspotOwners={hubspotAccountConnector?.owners}
            />

            <LoadingArea loading={!some([company.traits, company.enrichment])}>
              <AttributeBrowser
                onTabChange={handleTabChange}
                object={company}
                traits={company.traits}
                enrichment={company.enrichment}
                salesforceAccountConnector={salesforceAccountConnector}
                salesforceAccounts={salesforceAccounts}
                hubspotCompanies={hubspotCompanies}
                hubspotOwners={hubspotAccountConnector?.owners}
              />
            </LoadingArea>
          </div>
        )}
        {tabIndex === 1 && (
          <Activity company={company} className="flex flex-col flex-grow" />
        )}
        {tabIndex === 2 && (
          <People companyId={company.id} className="flex flex-col flex-grow" />
        )}
        {tabIndex === 3 && (
          <Segments company={company} className="flex flex-col flex-grow" />
        )}
      </CKTabs>
    </div>
  );
};

Company.propTypes = {
  id: PropTypes.string,
};

export default wrapError(Company);
